import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { withUiBlock } from '@bingads-webui/uiblocker';
import _ from 'underscore';
import { AccountSetupView } from '../account-setup/account-view';
import { BudgetBidSetupView } from '../budget-bid-setup/budget-bid-view';
import { HeaderView } from '../header/header-view';
import template from './host-template.pug';
import { SubAccountModel, instrumentation } from '../hotel-center-shared';

const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center-setup');
const { showError } = globalContext.require('component/toast/index');

/**
 * View class for Hotel Ads setup wizard host
 * @extends Backbone.View
 */
class HostView extends DeterministicView {
  initialize(options = {}) {
    this.options = options;
    this.steps = {
      account: 'account',
      budgetBid: 'budgetBid',
    };

    this.subAccountModel = new SubAccountModel();

    this.initHeader();
    this.initAccountView();
    this.initBudgetBidView();

    this.setState({ errorMessage: '' });

    this.subAccountSetupScenario = instrumentation.subAccountSetup.create();
    const initializeWizardActivity = this.subAccountSetupScenario.initializeWizard.create();

    initializeWizardActivity.trace('Sub-account setup wizard is initialized');
  }

  get template() {
    return template;
  }

  setState({ activeStep, errorMessage }) {
    if (_.has(this.steps, activeStep)) {
      this.model.set({
        activeStep,
      });
    }
    this.model.set({ errorMessage });
  }

  switchStep(newStep) {
    if (_.has(this.steps, newStep)) {
      this.setState({ activeStep: this.steps[newStep] });
    }
  }

  accountFinished() {
    const accountInfo = this.getChild(this.steps.account).getState();

    this.getChild(this.steps.budgetBid).setState(accountInfo);
    this.getChild('header').setState({ step: this.steps.budgetBid });
  }

  budgetBidFinished() {
    const saveSubAccountActivity = this.subAccountSetupScenario.saveSubAccount.create();

    saveSubAccountActivity.trace('Saving sub-account from setup wizard');

    this.setState({ errorMessage: '' });

    return saveSubAccountActivity.monitorAsync(this.subAccountModel.save())
      .then(() => {
        this.closeSetupWizard();
      }).catch((e) => {
        const error = this.mapErrors(e.errors);
        if (_.isEmpty(error)) {
          showError(i18n.getString('GenericErrorMessage'));
          saveSubAccountActivity.error(JSON.stringify(e));
        } else {
          this.setState({ errorMessage: error });
        }
      }).finally(withUiBlock(this.el));
  }

  initHeader() {
    const headerView = new HeaderView({
      steps: this.steps,
      strings: {
        account: i18n.getString('HotelCenter_Setup_Account'),
        budgetBid: i18n.getString('HotelCenter_Setup_Budget_Bid'),
      },
      i18n,
    });

    this.setChild('header', headerView);
    this.listenTo(headerView, 'step-changed', this.switchStep);
    headerView.setState({ step: this.steps.account });
  }

  initAccountView() {
    const accountSetupView = new AccountSetupView({
      subAccountModel: this.subAccountModel,
    });

    this.setChild(this.steps.account, accountSetupView);
    this.listenTo(accountSetupView, 'finished', this.accountFinished);
    this.listenTo(accountSetupView, 'cancelled', this.closeSetupWizard);
  }

  initBudgetBidView() {
    const budgetBidSetupView = new BudgetBidSetupView({
      subAccountModel: this.subAccountModel,
    });

    this.setChild(this.steps.budgetBid, budgetBidSetupView);
    this.listenTo(budgetBidSetupView, 'finished', this.budgetBidFinished);
    this.listenTo(budgetBidSetupView, 'cancelled', this.closeSetupWizard);
    this.listenTo(budgetBidSetupView, 'returned', () => {
      this.getChild('header').setState({ step: this.steps.account });
    });
  }

  closeSetupWizard() {
    const closeWizardActivity = this.subAccountSetupScenario.closeWizard.create();

    closeWizardActivity.trace('Closing setup wizard');

    if (this.options && this.options.isUINext &&
      this.options.router && _.isFunction(this.options.router.navigateToOverview)) {
      this.options.router.navigateToOverview();
    } else {
      window.location.href = '#overview';
    }
  }

  mapErrors(errors) {
    const errorMap = {
      /** fix bug3610009: encountered error when create subaccount with invalid Daily budget */
      /** use Error_Campaign_API_BudgetAmountIsAboveLimit Because it's more suitable */
      BudgetAmountIsAboveLimit: i18n.getString('Error_Campaign_API_BudgetAmountIsAboveLimit'),
      BudgetAmountIsBelowLimit: i18n.getString('HotelCenter_Error_BudgetAmountIsBelowLimit'),
      SameValueAlreadyExist: i18n.getString('HotelCenter_Error_SameValueAlreadyExist'),
      /** fix bug4616501: Bad error message displayed when create new subaccount */
      MaxSubAccountLimitationReached: i18n.getString('HotelCenter_Error_MaxSubAccountLimitationReached'),
      ValueTooLong: i18n.getString('Error_Campaign_API_ValueTooLong'),
    };

    return _.chain(errorMap)
      .pick(_.pluck(errors, 'Code'))
      .values()
      .first()
      .value();
  }
}


export {
  AccountSetupView,
  BudgetBidSetupView,
  HeaderView,
};

export const SetupView = HostView;
